import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Pencil, Save, X, FileDown, FileText, Sheet as SheetIcon,
  History, Eye, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MermaidDiagram } from '@/components/admin/MermaidDiagram';
import type { Components } from 'react-markdown';
import {
  adminApi,
  type ComplianceDocFull,
  type ComplianceDocStatus,
  type ComplianceVersionMeta,
  type ComplianceVersionFull,
} from '@/services/adminApi';
import { exportToDocx, exportTablesToExcel, printDocument } from '@/lib/admin/complianceExport';

const STATUS_LABEL: Record<ComplianceDocStatus, string> = {
  draft: 'Bozza',
  in_review: 'In revisione',
  approved: 'Approvato',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
}

const HUB_SLUGS = new Set([
  'risk-management-system',
  'fria-dpia',
  'technical-documentation',
  'post-market-monitoring',
  'gdpr-tech-debt',
]);

/** Markdown renderers: mermaid fences -> diagrams, code blocks, doc-to-doc links. */
const MARKDOWN_COMPONENTS: Components = {
  a({ href, children, ...props }) {
    const h = href ?? '';
    if (/^https?:\/\//.test(h)) {
      return <a href={h} target="_blank" rel="noreferrer" {...props}>{children}</a>;
    }
    if (h.endsWith('.md')) {
      const slug = h.replace(/^.*\//, '').replace(/\.md$/, '');
      if (HUB_SLUGS.has(slug)) return <Link to={`/admin/compliance/${slug}`}>{children}</Link>;
      return <span>{children}</span>;
    }
    if (h.startsWith('#')) return <a href={h} {...props}>{children}</a>;
    if (h.startsWith('/')) return <Link to={h}>{children}</Link>;
    return <a href={h} {...props}>{children}</a>;
  },
  code({ className, children, ...props }) {
    const text = String(children ?? '');
    if (/language-mermaid/.test(className ?? '')) {
      return <MermaidDiagram code={text.replace(/\n$/, '')} />;
    }
    if ((className ?? '').startsWith('language-') || text.includes('\n')) {
      return (
        <pre className="overflow-x-auto rounded bg-muted p-3 text-xs">
          <code className={className} {...props}>{children}</code>
        </pre>
      );
    }
    return <code className={className} {...props}>{children}</code>;
  },
  pre({ children }) {
    return <>{children}</>;
  },
};

export default function AdminComplianceDocumentPage() {
  const { slug } = useParams<{ slug: string }>();
  const [doc, setDoc] = useState<ComplianceDocFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draftContent, setDraftContent] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [draftStatus, setDraftStatus] = useState<ComplianceDocStatus>('draft');
  const [saving, setSaving] = useState(false);

  const [versions, setVersions] = useState<ComplianceVersionMeta[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [preview, setPreview] = useState<ComplianceVersionFull | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const d = await adminApi.compliance.get(slug);
      setDoc(d);
      setDraftStatus(d.status);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadVersions = useCallback(async () => {
    if (!slug) return;
    const res = await adminApi.compliance.versions(slug);
    setVersions(res.items);
  }, [slug]);

  const startEdit = () => {
    if (!doc) return;
    setDraftContent(doc.content);
    setDraftStatus(doc.status);
    setChangeNote('');
    setPreview(null);
    setEditing(true);
  };

  const save = async () => {
    if (!slug) return;
    if (!changeNote.trim()) {
      toast.error('Inserisci una nota di modifica (changelog).');
      return;
    }
    setSaving(true);
    try {
      await adminApi.compliance.update(slug, {
        content: draftContent,
        reason: changeNote.trim(),
        status: draftStatus,
      });
      toast.success('Documento salvato — nuova versione creata.');
      setEditing(false);
      setVersions(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Salvataggio fallito');
    } finally {
      setSaving(false);
    }
  };

  const onExcel = () => {
    if (!doc) return;
    const content = preview?.content ?? doc.content;
    const n = exportTablesToExcel(slug ?? 'document', content);
    if (n === 0) toast.info('Nessuna tabella da esportare in Excel in questo documento.');
  };

  const onDocx = async () => {
    if (!doc) return;
    setExporting(true);
    try {
      await exportToDocx(slug ?? 'document', doc.title, preview?.content ?? doc.content);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export DOCX fallito');
    } finally {
      setExporting(false);
    }
  };

  const viewVersion = async (version: number) => {
    if (!slug) return;
    if (doc && version === doc.currentVersion) {
      setPreview(null);
      return;
    }
    const v = await adminApi.compliance.version(slug, version);
    setPreview(v);
  };

  const renderedContent = preview?.content ?? doc?.content ?? '';

  return (
    <div className="max-w-4xl">
      <style>{`
        @media print {
          body.printing-compliance * { visibility: hidden !important; }
          body.printing-compliance #compliance-print-area,
          body.printing-compliance #compliance-print-area * { visibility: visible !important; }
          body.printing-compliance #compliance-print-area {
            position: absolute; left: 0; top: 0; width: 100%; padding: 24px;
          }
        }
      `}</style>

      <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
        <Link to="/admin/compliance">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Compliance
        </Link>
      </Button>

      {loading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {error && (
        <p className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
          {error}
        </p>
      )}

      {doc && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-xl font-semibold flex-1 min-w-0">{doc.title}</h1>
            <Badge variant="secondary">{STATUS_LABEL[doc.status]}</Badge>
            <Badge variant="outline">v{doc.currentVersion}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Aggiornato {fmtDate(doc.updatedAt)} {doc.updatedBy ? `· ${doc.updatedBy}` : ''}
          </p>

          {/* Toolbar */}
          {!editing && (
            <div className="flex flex-wrap gap-2 mb-4">
              <Button size="sm" variant="outline" onClick={printDocument}>
                <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
              </Button>
              <Button size="sm" variant="outline" onClick={onDocx} disabled={exporting}>
                {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />} DOCX
              </Button>
              <Button size="sm" variant="outline" onClick={onExcel}>
                <SheetIcon className="h-3.5 w-3.5 mr-1.5" /> Excel (tabelle)
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowHistory((s) => !s);
                  if (!versions) void loadVersions();
                }}
              >
                <History className="h-3.5 w-3.5 mr-1.5" /> Versioni
              </Button>
              <div className="flex-1" />
              <Button size="sm" onClick={startEdit}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifica
              </Button>
            </div>
          )}

          {/* Version history */}
          {showHistory && !editing && (
            <Card className="mb-4">
              <CardContent className="py-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">Storico versioni</p>
                {!versions && <p className="text-xs text-muted-foreground">Caricamento…</p>}
                <div className="space-y-1">
                  {versions?.map((v) => {
                    const isCurrent = doc.currentVersion === v.version;
                    const isPreviewing = preview?.version === v.version;
                    return (
                      <div key={v.version} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className="shrink-0">v{v.version}</Badge>
                        <span className="text-xs text-muted-foreground flex-1 min-w-0 truncate">
                          {fmtDate(v.createdAt)} · {v.createdBy ?? '—'} · {v.changeNote ?? ''}
                        </span>
                        {isCurrent ? (
                          <Badge variant="secondary" className="shrink-0">corrente</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant={isPreviewing ? 'secondary' : 'ghost'}
                            className="h-7 shrink-0"
                            onClick={() => void viewVersion(v.version)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" /> {isPreviewing ? 'In anteprima' : 'Vedi'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {preview && !editing && (
            <div className="mb-3 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
              <Eye className="h-3.5 w-3.5" />
              Stai visualizzando la versione storica v{preview.version}.
              <button className="underline" onClick={() => setPreview(null)}>
                Torna alla versione corrente
              </button>
            </div>
          )}

          {/* Editor */}
          {editing ? (
            <Card>
              <CardContent className="py-4 space-y-3">
                <Textarea
                  value={draftContent}
                  onChange={(e) => setDraftContent(e.target.value)}
                  className="font-mono text-xs min-h-[480px]"
                />
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-muted-foreground">Nota di modifica (changelog) *</label>
                    <Input
                      value={changeNote}
                      onChange={(e) => setChangeNote(e.target.value)}
                      placeholder="Es. revisione sezione rischi R3"
                      className="h-9 mt-1"
                    />
                  </div>
                  <div className="w-40">
                    <label className="text-xs font-medium text-muted-foreground">Stato</label>
                    <Select value={draftStatus} onValueChange={(v) => setDraftStatus(v as ComplianceDocStatus)}>
                      <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Bozza</SelectItem>
                        <SelectItem value="in_review">In revisione</SelectItem>
                        <SelectItem value="approved">Approvato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                      <X className="h-3.5 w-3.5 mr-1.5" /> Annulla
                    </Button>
                    <Button size="sm" onClick={() => void save()} disabled={saving}>
                      {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Salva versione
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-6">
                <article
                  id="compliance-print-area"
                  className="prose prose-slate prose-sm md:prose-base max-w-none dark:prose-invert"
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{renderedContent}</ReactMarkdown>
                </article>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
